import React, { useState, useEffect } from 'react';
import { TablePaginationConfig } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { useSetRecoilState } from 'recoil';
import { useHistory, useLocation } from 'react-router';
import { useUpdateEffect } from 'usehooks-ts';

import './index.less';

import {
  OCRProviderCreationRequest,
  OCRProviderModel,
  OCRProviderUpdatingRequest,
  ProviderModel,
} from '../../../../gamma-api';
import { defaultApi } from '../../../apis';
import { ValidationErrors } from '../../../types';
import { getPageFromSearchParams, PAGE_INDEX_PARAM } from '../../../utils/urlUtil';
import { getTranslateFunc } from '../../../utils/stringUtil';
import { tryGetValidationErrors } from '../../../utils/errorUtils';
import { alertsSelectorAdd } from '../../../recoil';
import IbIcon from '../../../components/common/IbIcon';
import IbTypography from '../../../components/common/IbTypography';
import IbButton from '../../../components/common/IbButton';
import IbPageTitle from '../../../components/IbPageTitle';
import IbPagination from '../../../components/common/IbPagination';
import IbTable from '../../../components/common/IbTable';
import IbContextMenu, { IIbContextMenuItem } from '../../../components/common/IbContextMenu';
import IbTextHighlighter from '../../../components/common/IbTextHighlighter';
import IbTooltip from '../../../components/common/IbTooltip';
import { LARGE_PAGE_SIZE } from '../../../constants';

import {
  CONTENT_CLASS_NAME,
  OCR_PROVIDERS_PAGE_SIZE,
  MAIN_CLASS_NAME,
  NAME_CELL_CLASS_NAME,
  PAGINATION_CLASS_NAME,
  TOOLBAR_CLASS_NAME,
  TOOLTIP_MOUSE_DELAY,
  TRANSLATION_GROUP_PREFIX,
  TYPE_CELL_CLASS_NAME,
} from './const';
import ConfirmModal from './ConfirmModal';
import OcrProviderModal from './OcrProviderModal';

const OcrProvidersPage: React.FC = () => {
  const location = useLocation();
  const { push } = useHistory();

  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: getPageFromSearchParams(location.search),
    pageSize: OCR_PROVIDERS_PAGE_SIZE,
    total: 0,
  });

  const [searchText, setSearchText] = useState('');
  const [llmProviders, setLlmProviders] = useState<ProviderModel[]>([]);
  const [ocrProviders, setOcrProviders] = useState<OCRProviderModel[]>([]);

  const [llmProvidersLoading, setLlmProvidersLoading] = useState(true);
  const [ocrProvidersLoading, setOcrProvidersLoading] = useState(true);
  const [ocrProviderDeleting, setOcrProviderDeleting] = useState(false);

  const [ocrProviderModalVisible, setOcrProviderModalVisible] = useState(false);

  const [contextOcrProvider, setContextOcrProvider] = useState<OCRProviderModel>();
  const [modalOcrProvider, setModalOcrProvider] = useState<OCRProviderModel>();
  const [deleteOcrProvider, setDeleteOcrProvider] = useState<OCRProviderModel>();

  const translate = getTranslateFunc(TRANSLATION_GROUP_PREFIX);

  const loadLlmProviders = async (withLoading: boolean) => {
    withLoading && setLlmProvidersLoading(true);

    try {
      const response = await defaultApi.searchProvidersGeneralProvidersGet(0, LARGE_PAGE_SIZE, true);
      setLlmProviders(response.data.items || []);
    } catch (err) {
      addAlert({
        content: translate('Failed to load'),
        type: 'error',
      });
      // eslint-disable-next-line no-console
      console.error(err);
    }

    withLoading && setLlmProvidersLoading(false);
  };

  const loadOcrProviders = async (withLoading: boolean) => {
    withLoading && setOcrProvidersLoading(true);

    try {
      const response = await defaultApi.searchOcrProviders(
        (pagination.current || 1) - 1,
        OCR_PROVIDERS_PAGE_SIZE,
        true,
        searchText
      );
      setOcrProviders(response.data.items || []);
      setPagination((prev) => ({ ...prev, total: response.data.total_item_count || 0 }));
    } catch (err) {
      addAlert({
        content: translate('Failed to load'),
        type: 'error',
      });
      // eslint-disable-next-line no-console
      console.error(err);
    }

    withLoading && setOcrProvidersLoading(false);
  };

  useEffect(() => {
    loadLlmProviders(true).finally();
  }, []);

  useEffect(() => {
    loadOcrProviders(true).finally();
  }, [pagination.current, pagination.pageSize, searchText]);

  useEffect(() => {
    const page = getPageFromSearchParams(location.search);
    if (page !== pagination.current) {
      setPagination((prev) => ({ ...prev, current: page }));
    }
  }, [location.search]);

  useUpdateEffect(() => {
    if (pagination.current !== 1) {
      setPagination({ ...pagination, current: 1 });
    }
  }, [searchText]);

  useUpdateEffect(() => {
    if (pagination.current) {
      const params = new URLSearchParams(location.search);
      params.set(PAGE_INDEX_PARAM, pagination.current.toString());
      push({ pathname: location.pathname, search: params.toString() });
    }
  }, [pagination.current]);

  const resetProviderModal = () => {
    setOcrProviderModalVisible(false);
    setModalOcrProvider(undefined);
  };

  const onCreateProviderClick = () => {
    setOcrProviderModalVisible(true);
  };

  const onProviderModalCancel = () => {
    resetProviderModal();
  };

  const onProviderCreate = async (
    request: OCRProviderCreationRequest,
    onSubmitErrors: (errors: ValidationErrors) => void
  ): Promise<void> => {
    try {
      await defaultApi.createOcrProvider(request);
      await loadOcrProviders(false);
      resetProviderModal();
    } catch (e) {
      const validationErrors = tryGetValidationErrors(e as Error);

      if (validationErrors) {
        onSubmitErrors(validationErrors);
      } else {
        addAlert({
          content: translate('Failed to create'),
          type: 'error',
          icon: <IbIcon iconName="attention" />,
        });
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  };

  const onProviderUpdate = async (
    request: OCRProviderUpdatingRequest,
    onSubmitErrors: (errors: ValidationErrors) => void
  ): Promise<void> => {
    if (!modalOcrProvider) return;

    try {
      await defaultApi.updateOcrProvider(modalOcrProvider.ocr_provider_id, request);
      await loadOcrProviders(false);
      resetProviderModal();
    } catch (e) {
      const validationErrors = tryGetValidationErrors(e as Error);

      if (validationErrors) {
        onSubmitErrors(validationErrors);
      } else {
        addAlert({
          content: translate('Failed to update'),
          type: 'error',
          icon: <IbIcon iconName="attention" />,
        });
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  };

  const onDeleteConfirmButtonClick = async () => {
    if (!deleteOcrProvider) {
      return;
    }

    setOcrProviderDeleting(true);

    try {
      await defaultApi.deleteOcrProvider(deleteOcrProvider.ocr_provider_id);
    } catch (e) {
      addAlert({
        content: translate('Failed to delete'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
      // eslint-disable-next-line no-console
      console.error(e);
    }

    await loadOcrProviders(false);
    setOcrProviderDeleting(false);
    setDeleteOcrProvider(undefined);
  };

  const onDeleteCancelButtonClick = () => {
    setDeleteOcrProvider(undefined);
  };

  const onTableRowClick = (provider: OCRProviderModel, column: ColumnType<OCRProviderModel>) => {
    if (!column.title) {
      return;
    }

    setModalOcrProvider(provider);
    setOcrProviderModalVisible(true);
  };

  const onPaginationChange = (page: number) => setPagination({ ...pagination, current: page });

  const onSearchTextChange = (value: string) => setSearchText(value);

  const renderNameCell = (provider: OCRProviderModel) => {
    const text = provider.name;

    return (
      <div className={NAME_CELL_CLASS_NAME}>
        <IbTooltip mouseEnterDelay={TOOLTIP_MOUSE_DELAY} title={text}>
          <IbTypography>
            <IbTypography.Paragraph type="secondary">
              <IbTextHighlighter highlightedText={searchText} text={text} />
            </IbTypography.Paragraph>
          </IbTypography>
        </IbTooltip>
      </div>
    );
  };

  const renderTypeCell = (provider: OCRProviderModel) => {
    const text = provider.settings.type;

    return (
      <div className={TYPE_CELL_CLASS_NAME}>
        <IbTooltip mouseEnterDelay={TOOLTIP_MOUSE_DELAY} title={text}>
          <IbTypography>
            <IbTypography.Paragraph type="secondary">{text}</IbTypography.Paragraph>
          </IbTypography>
        </IbTooltip>
      </div>
    );
  };

  const renderContextMenu = (provider: OCRProviderModel) => {
    const onUpdateClick = () => {
      setModalOcrProvider(provider);
      setOcrProviderModalVisible(true);
    };

    const onDeleteClick = () => setDeleteOcrProvider(provider);

    const onMenuOpen = () => setContextOcrProvider(provider);

    const onMenuClose = () => setContextOcrProvider(undefined);

    const menuItems: IIbContextMenuItem[] = [];

    menuItems.push({
      icon: <IbIcon iconName="edit" />,
      text: translate('Context: Update'),
      onSelect: onUpdateClick,
    });

    menuItems.push({
      icon: <IbIcon iconName="delete" />,
      text: translate('Context: Delete'),
      onSelect: onDeleteClick,
    });

    return (
      <IbContextMenu
        menuItems={menuItems}
        visible={contextOcrProvider?.ocr_provider_id === provider.ocr_provider_id}
        onClose={onMenuClose}
      >
        <IbButton icon={<IbIcon iconName="more-one" />} type="icon" onClick={onMenuOpen}></IbButton>
      </IbContextMenu>
    );
  };

  const renderToolbar = () => {
    return (
      <div className={TOOLBAR_CLASS_NAME}>
        <IbButton icon={<IbIcon iconName="add-one" />} onClick={onCreateProviderClick}>
          {translate('Toolbar: Create')}
        </IbButton>
      </div>
    );
  };

  const renderTable = () => {
    const columns: ColumnType<OCRProviderModel>[] = [
      {
        title: translate('Table: Name'),
        width: '60%',
        render: (_, provider) => renderNameCell(provider),
      },
      {
        title: translate('Table: Type'),
        width: '40%',
        render: (_, provider) => renderTypeCell(provider),
      },
      {
        title: '',
        render: (_, provider) => renderContextMenu(provider),
      },
    ];

    return (
      <IbTable
        columns={columns}
        dataSource={ocrProviders}
        emptyText={<IbTypography.Paragraph disabled>{translate('Nothing found')}</IbTypography.Paragraph>}
        loading={ocrProvidersLoading}
        rowKey={(provider: OCRProviderModel) => provider.ocr_provider_id}
        onRowClick={onTableRowClick}
      />
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      <IbPageTitle
        showSearchInput
        searchPlaceholder={translate('Search: Placeholder')}
        title={translate('Title')}
        onDebouncedSearch={onSearchTextChange}
      />
      <div className={CONTENT_CLASS_NAME}>
        {renderToolbar()}
        {renderTable()}
      </div>
      {(pagination.total ?? 0) > OCR_PROVIDERS_PAGE_SIZE && (
        <div className={PAGINATION_CLASS_NAME}>
          <IbPagination
            current={pagination.current}
            defaultCurrent={1}
            defaultPageSize={OCR_PROVIDERS_PAGE_SIZE}
            pageSize={OCR_PROVIDERS_PAGE_SIZE}
            showSizeChanger={false}
            total={pagination.total}
            onChange={onPaginationChange}
          />
        </div>
      )}
      <OcrProviderModal
        editOcrProvider={modalOcrProvider}
        llmProviders={llmProviders}
        llmProvidersLoading={llmProvidersLoading}
        visible={ocrProviderModalVisible}
        onCancel={onProviderModalCancel}
        onCreate={onProviderCreate}
        onUpdate={onProviderUpdate}
      />
      <ConfirmModal.DeleteOcrProvider
        loading={ocrProviderDeleting}
        visible={!!deleteOcrProvider}
        onCancel={onDeleteCancelButtonClick}
        onConfirm={onDeleteConfirmButtonClick}
      />
    </div>
  );
};

export default OcrProvidersPage;
